import React, { useState, useEffect, useReducer, useCallback, useRef, useContext } from 'react';
import { AppContext } from '../context/AppContext';

// ─── Formatters ─────────────────────────────────────────────────────────────
const fmt  = (v) => Number(v || 0).toLocaleString('fr-FR');
const fmtK = (v) => {
    const n = Number(v || 0);
    if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (Math.abs(n) >= 1_000)     return (n / 1_000).toFixed(0) + 'K';
    return fmt(n);
};
const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ─── State Management ────────────────────────────────────────────────────────
const initialState = {
    // filters
    month:     new Date().getMonth() + 1,
    year:      new Date().getFullYear(),
    date:      new Date().toISOString().split('T')[0],
    depoId:    null,
    // data
    data:      null,
    loading:   true,
    error:     null,
    // modals
    ledgerOpen:  false,
    achatOpen:   false,
    closingOpen: false,
    // optimistic
    dayClosing:  false,
    achatSaving: false,
};
function reducer(state, action) {
    switch (action.type) {
        case 'SET_FILTER':   return { ...state, ...action.payload };
        case 'LOAD_START':   return { ...state, loading: true, error: null };
        case 'LOAD_SUCCESS': return { ...state, loading: false, data: action.payload };
        case 'LOAD_ERROR':   return { ...state, loading: false, error: action.payload };
        case 'OPEN_MODAL':   return { ...state, [`${action.modal}Open`]: true };
        case 'CLOSE_MODAL':  return { ...state, [`${action.modal}Open`]: false };
        case 'SET_CLOSING':  return { ...state, dayClosing: action.value };
        case 'SET_SAVING':   return { ...state, achatSaving: action.value };
        default: return state;
    }
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const KpiCard = React.memo(({ label, value, sub, colorClass = 'border-slate-400', textClass = 'text-slate-800', prefix = '' }) => (
    <div className={`bg-white rounded-xl p-5 shadow-sm border-t-4 ${colorClass} transition hover:shadow-md`}>
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
        <p className={`text-2xl font-black ${textClass}`}>{prefix}{fmtK(value)} <span className="text-xs font-normal text-slate-400">CFA</span></p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
));

const SectionHeader = ({ icon, title, subtitle, right }) => (
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
        <div>
            <h2 className="text-xl font-black text-slate-800 flex items-center gap-2">{icon} {title}</h2>
            {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
        {right}
    </div>
);

// ── Section 1: Network Grid ────────────────────────────────────────────────
const Section1Network = React.memo(({ data }) => {
    const { totals, branches } = data;
    const netPositive = totals.net_profit_loss >= 0;

    return (
        <section>
            <SectionHeader icon="🌐" title="Multi-Branch Network Overview"
                subtitle="Real-time inventory valuation & consolidated profitability" />

            {/* Network KPI Banner */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <KpiCard label="Total Stock Cost"     value={totals.stock_cost_value}  colorClass="border-slate-500" />
                <KpiCard label="Expected Sell Value"  value={totals.expected_sell_value} colorClass="border-blue-500" textClass="text-blue-700"/>
                <KpiCard label="Monthly Revenue"      value={totals.monthly_revenue}   colorClass="border-emerald-500" textClass="text-emerald-700"/>
                <KpiCard label="Monthly Expenses"     value={totals.monthly_expenses}  colorClass="border-orange-500" textClass="text-orange-700"/>
                <KpiCard label="Net Profit / Loss"
                    value={Math.abs(totals.net_profit_loss)}
                    prefix={netPositive ? '+' : '−'}
                    colorClass={netPositive ? 'border-green-500' : 'border-red-500'}
                    textClass={netPositive ? 'text-green-700' : 'text-red-700'}/>
            </div>

            {/* Branch Table */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-800 text-white">
                                {['Branch','Stock Cost','Sell Value','Revenue','Expenses','Net Gain/Loss'].map(h => (
                                    <th key={h} className={`px-4 py-3 font-bold text-xs uppercase tracking-wide ${h==='Branch'?'text-left':'text-right'}`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {branches.map((b) => {
                                const isLoss = b.net_profit_loss < 0;
                                return (
                                    <React.Fragment key={b.branch_id}>
                                        <tr className={`border-b border-slate-50 hover:bg-slate-50 transition ${isLoss ? 'bg-red-50/30' : ''}`}>
                                            <td className="px-4 py-3 font-semibold text-slate-800">{b.branch_name}</td>
                                            <td className="px-4 py-3 text-right text-slate-600">{fmt(b.stock_cost_value)}</td>
                                            <td className="px-4 py-3 text-right text-blue-600">{fmt(b.expected_sell_value)}</td>
                                            <td className="px-4 py-3 text-right text-emerald-600 font-medium">{fmt(b.monthly_revenue)}</td>
                                            <td className="px-4 py-3 text-right text-orange-500">{fmt(b.monthly_expenses)}</td>
                                            <td className={`px-4 py-3 text-right font-bold ${isLoss ? 'text-red-600' : 'text-green-700'}`}>
                                                {isLoss ? '−' : '+'}{fmt(Math.abs(b.net_profit_loss))}
                                            </td>
                                        </tr>
                                        {b.alert && (
                                            <tr className="bg-red-50 border-b border-red-100">
                                                <td colSpan={6} className="px-4 py-2 text-xs text-red-700 font-medium flex items-center gap-1.5">
                                                    ⚠️ {b.alert}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {branches.length === 0 && (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No branch data available.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
});

// ── Section 2: Monthly Matrix ─────────────────────────────────────────────
const Section2Matrix = React.memo(({ data, month, year, onMonthChange, onYearChange }) => {
    const { rows, monthly_totals: totals } = data;

    const exportCSV = useCallback(() => {
        const header = 'Date,Cash Sales,Visa Sales,Credit Sales,Total Sales,Achats,Depenses,Net Cash Flow\n';
        const lines  = rows.map(r =>
            `${r.date},${r.cash_sales},${r.visa_sales},${r.credit_sales},${r.total_daily_sales},${r.accepted_achats},${r.depenses},${r.net_daily_cash_flow}`
        ).join('\n');
        const blob = new Blob([header + lines], { type: 'text/csv' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `matrix_${year}_${String(month).padStart(2,'0')}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
    }, [rows, month, year]);

    return (
        <section>
            <SectionHeader icon="📅" title="Monthly Sales & Outflow Matrix"
                subtitle={`Day-by-day financial breakdown — ${MONTHS_FULL[month-1]} ${year}`}
                right={
                    <div className="flex items-center gap-2 flex-wrap">
                        <select value={month} onChange={e => onMonthChange(Number(e.target.value))}
                            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400 bg-white">
                            {MONTHS_FULL.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
                        </select>
                        <input type="number" value={year} min="2020" max="2099"
                            onChange={e => onYearChange(Number(e.target.value))}
                            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm w-20 outline-none focus:ring-2 focus:ring-indigo-400"/>
                        <button onClick={exportCSV}
                            className="bg-slate-700 hover:bg-slate-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition">
                            ⬇ CSV
                        </button>
                    </div>
                }
            />

            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                        <thead>
                            <tr className="bg-slate-700 text-white">
                                {['Day','Cash Sales','Visa Sales','Credit Sales','Total Sales','Achats','Depenses','Net Cash Flow','Status'].map(h => (
                                    <th key={h} className={`px-3 py-2.5 font-bold text-xs uppercase tracking-wide whitespace-nowrap ${h==='Day'||h==='Status'?'text-left':'text-right'}`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => {
                                const hasActivity = row.total_daily_sales > 0 || row.accepted_achats > 0 || row.depenses > 0;
                                const positive    = row.net_daily_cash_flow >= 0;
                                return (
                                    <tr key={row.date} className={`border-b border-slate-50 ${hasActivity?'hover:bg-blue-50':'bg-slate-50/50 text-slate-400'} transition`}>
                                        <td className="px-3 py-2 font-medium text-slate-700 whitespace-nowrap">
                                            {row.day_number} {MONTHS[month-1]}
                                        </td>
                                        <td className="px-3 py-2 text-right">{hasActivity ? fmt(row.cash_sales)    : '—'}</td>
                                        <td className="px-3 py-2 text-right">{hasActivity ? fmt(row.visa_sales)    : '—'}</td>
                                        <td className="px-3 py-2 text-right">{hasActivity ? fmt(row.credit_sales)  : '—'}</td>
                                        <td className="px-3 py-2 text-right font-semibold text-blue-600">{hasActivity ? fmt(row.total_daily_sales) : '—'}</td>
                                        <td className="px-3 py-2 text-right text-red-500">{hasActivity ? fmt(row.accepted_achats)  : '—'}</td>
                                        <td className="px-3 py-2 text-right text-orange-500">{hasActivity ? fmt(row.depenses)       : '—'}</td>
                                        <td className={`px-3 py-2 text-right font-bold ${positive?'text-green-600':'text-red-600'}`}>
                                            {hasActivity ? `${positive?'+':'−'}${fmt(Math.abs(row.net_daily_cash_flow))}` : '—'}
                                        </td>
                                        <td className="px-3 py-2">
                                            {row.day_status && (
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold
                                                    ${row.day_status === 'Closed' ? 'bg-slate-200 text-slate-600' : 'bg-green-100 text-green-700'}`}>
                                                    {row.day_status === 'Closed' ? '🔒' : '🟢'} {row.day_status}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="bg-indigo-900 text-white font-black text-sm">
                                <td className="px-3 py-3">MONTH TOTAL</td>
                                <td className="px-3 py-3 text-right">{fmt(totals.cash_sales)}</td>
                                <td className="px-3 py-3 text-right">{fmt(totals.visa_sales)}</td>
                                <td className="px-3 py-3 text-right">{fmt(totals.credit_sales)}</td>
                                <td className="px-3 py-3 text-right text-blue-200">{fmt(totals.total_sales)}</td>
                                <td className="px-3 py-3 text-right text-red-300">{fmt(totals.accepted_achats)}</td>
                                <td className="px-3 py-3 text-right text-orange-300">{fmt(totals.depenses)}</td>
                                <td className={`px-3 py-3 text-right text-xl ${totals.net_cash_flow >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                                    {totals.net_cash_flow >= 0 ? '+' : '−'}{fmt(Math.abs(totals.net_cash_flow))}
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </section>
    );
});

// ── Section 3: EOD Closing & Ledger ──────────────────────────────────────
const Section3EOD = React.memo(({ data, onOpenLedger, onOpenAchat, onCloseDay, dayClosing }) => {
    const { live_snapshot: snap, is_day_closed, date } = data;
    const netPositive = snap.expected_physical_cash >= 0;

    return (
        <section>
            <SectionHeader icon="🏦" title="End-of-Day Closing & Ledger"
                subtitle={`Live snapshot for ${date}`}
                right={
                    <div className="flex items-center gap-2">
                        <button onClick={onOpenLedger}
                            className="bg-slate-600 hover:bg-slate-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition">
                            📒 View Ledger
                        </button>
                        {!is_day_closed && (
                            <button onClick={onOpenAchat}
                                className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold px-4 py-2 rounded-lg transition">
                                + Manual Achat
                            </button>
                        )}
                    </div>
                }
            />

            {/* Live Snapshot Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                <KpiCard label="Cash Sales Today"  value={snap.cash_total}   colorClass="border-blue-500" textClass="text-blue-700"/>
                <KpiCard label="Visa / Card"        value={snap.visa_total}   colorClass="border-purple-500" textClass="text-purple-700"/>
                <KpiCard label="Achats Today"       value={snap.total_achats} colorClass="border-red-400" textClass="text-red-700"/>
                <KpiCard label="Depenses Today"     value={snap.total_depenses} colorClass="border-orange-400" textClass="text-orange-700"/>
            </div>

            {/* Expected Physical Cash Banner */}
            <div className={`rounded-2xl p-6 shadow-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4
                ${is_day_closed
                    ? 'bg-gradient-to-r from-slate-700 to-slate-900'
                    : netPositive
                        ? 'bg-gradient-to-r from-emerald-600 to-teal-700'
                        : 'bg-gradient-to-r from-red-600 to-rose-800'}
                print:bg-none print:border-2 print:border-slate-300`}>
                <div className="text-white">
                    <p className="text-xs font-bold uppercase tracking-widest opacity-75">Expected Physical Cash in Drawer</p>
                    <p className="text-4xl font-black mt-1">
                        {netPositive ? '+' : '−'}{fmt(Math.abs(snap.expected_physical_cash))} CFA
                    </p>
                    <p className="text-xs opacity-60 mt-1">= Cash Sales − Drawer Achats − Depenses · {snap.invoice_count} invoices</p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    {is_day_closed ? (
                        <span className="bg-white/20 text-white font-black px-6 py-3 rounded-xl flex items-center gap-2">
                            🔒 Day Locked
                        </span>
                    ) : (
                        <button onClick={onCloseDay} disabled={dayClosing}
                            className="bg-white text-slate-900 font-black px-6 py-3 rounded-xl shadow hover:bg-slate-100 transition disabled:opacity-50 flex items-center gap-2">
                            {dayClosing ? '⏳ Closing...' : '🔒 Close Day'}
                        </button>
                    )}
                </div>
            </div>
        </section>
    );
});

// ── Ledger Modal ────────────────────────────────────────────────────────────
const LedgerModal = React.memo(({ entries, date, onClose }) => {
    const totalDebit  = entries.reduce((s, e) => s + e.debit, 0);
    const totalCredit = entries.reduce((s, e) => s + e.credit, 0);

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 print:hidden">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200">
                    <div>
                        <h3 className="text-xl font-black text-slate-800">📒 Double-Entry Ledger</h3>
                        <p className="text-sm text-slate-500">Full Month ({date.substring(0, 7)}) — {entries.length} entries</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">&times;</button>
                </div>
                <div className="overflow-auto flex-1">
                    <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-sm border-collapse">
                        <thead className="sticky top-0 bg-slate-100">
                            <tr>
                                {['Timestamp','Type','Account / Source','Ref','Debit (+)','Credit (−)','Balance','Logged By'].map(h => (
                                    <th key={h} className={`px-4 py-3 font-bold text-xs uppercase text-slate-600 ${['Debit (+)','Credit (−)','Balance'].includes(h)?'text-right':'text-left'}`}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {entries.length === 0 && (
                                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No ledger entries for this date.</td></tr>
                            )}
                            {entries.map((e) => (
                                <tr key={e.id} className="border-b border-slate-50 hover:bg-slate-50">
                                    <td className="px-4 py-2.5 whitespace-nowrap text-xs text-slate-500">{e.timestamp?.slice(0,16)}</td>
                                    <td className="px-4 py-2.5">
                                        <span className="inline-block px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 font-bold text-xs">{e.transaction_type}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-slate-700">{e.account_source}</td>
                                    <td className="px-4 py-2.5 text-xs text-slate-400">{e.reference_type ? `${e.reference_type}#${e.reference_id}` : '—'}</td>
                                    <td className="px-4 py-2.5 text-right font-semibold text-green-600">{e.debit > 0 ? fmt(e.debit) : '—'}</td>
                                    <td className="px-4 py-2.5 text-right font-semibold text-red-600">{e.credit > 0 ? fmt(e.credit) : '—'}</td>
                                    <td className={`px-4 py-2.5 text-right font-bold ${e.running_balance >= 0 ? 'text-slate-700' : 'text-red-600'}`}>{fmt(e.running_balance)}</td>
                                    <td className="px-4 py-2.5 text-xs text-slate-500">{e.logged_by_name || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-slate-800 text-white font-bold">
                            <tr>
                                <td colSpan={4} className="px-4 py-3">TOTALS</td>
                                <td className="px-4 py-3 text-right text-green-300">{fmt(totalDebit)}</td>
                                <td className="px-4 py-3 text-right text-red-300">{fmt(totalCredit)}</td>
                                <td className="px-4 py-3 text-right">{fmt(totalDebit - totalCredit)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
</div>
                </div>
            </div>
        </div>
    );
});

// ── Manual Achat Modal ────────────────────────────────────────────────────
const ManualAchatModal = React.memo(({ onClose, onSave, saving, date }) => {
    const [form, setForm] = useState({ supplier: '', amount: '', payment_source: 'Daily Cash Drawer', description: '' });
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const SOURCES = ['Daily Cash Drawer', 'Cash Capital', 'Bank Transfer / Visa', 'Petty Cash'];
    const drawerImpact = form.payment_source === 'Daily Cash Drawer';

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 print:hidden">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200">
                    <h3 className="text-xl font-black text-slate-800">+ Log Manual Achat</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-2xl leading-none">&times;</button>
                </div>
                <form className="p-6 space-y-4" onSubmit={e => { e.preventDefault(); onSave(form); }}>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase">Supplier / Description *</label>
                        <input required type="text" value={form.supplier} onChange={e => set('supplier', e.target.value)}
                            placeholder="e.g. Supplier name or purpose"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400"/>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase">Amount (CFA) *</label>
                        <input required type="number" min="1" value={form.amount} onChange={e => set('amount', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400"/>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase">Payment Source *</label>
                        <select value={form.payment_source} onChange={e => set('payment_source', e.target.value)}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400 bg-white">
                            {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>

                    {/* Business logic hint */}
                    <div className={`p-3 rounded-xl text-xs font-medium border ${drawerImpact
                        ? 'bg-orange-50 border-orange-200 text-orange-700'
                        : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                        {drawerImpact
                            ? '⚠️ Funded from Daily Cash Drawer — this will deduct from your physical closing cash balance.'
                            : '✓ Funded from external capital / bank — no impact on the physical cash drawer balance.'}
                    </div>

                    <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase">Notes</label>
                        <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400"/>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose}
                            className="px-5 py-2 border border-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50 transition">Cancel</button>
                        <button type="submit" disabled={saving}
                            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition disabled:opacity-50">
                            {saving ? 'Saving...' : 'Save Achat'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
});

// ── Close Day Confirm Modal ────────────────────────────────────────────────
const CloseDayModal = React.memo(({ snap, onConfirm, onClose, saving }) => {
    const [actualCash, setActualCash] = useState('');
    const [notes, setNotes] = useState('');
    const diff = parseFloat(actualCash || 0) - snap.expected_physical_cash;

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 print:hidden">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="text-xl font-black text-slate-800">🔒 Confirm Close Day</h3>
                    <p className="text-sm text-red-600 font-medium mt-1">⚠️ This action is irreversible. The day will be locked.</p>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        {[
                            ['System Cash',   snap.cash_total],
                            ['System Visa',   snap.visa_total],
                            ['Achats',        snap.total_achats],
                            ['Depenses',      snap.total_depenses],
                        ].map(([l,v]) => (
                            <div key={l} className="bg-slate-50 rounded-lg p-3">
                                <p className="text-slate-500 text-xs">{l}</p>
                                <p className="font-bold">{fmt(v)} CFA</p>
                            </div>
                        ))}
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-3 text-sm">
                        <p className="text-slate-500 text-xs">Expected Physical Cash</p>
                        <p className="font-black text-xl text-indigo-700">{fmt(snap.expected_physical_cash)} CFA</p>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase">Actual Cash Count (CFA)</label>
                        <input type="number" value={actualCash} onChange={e => setActualCash(e.target.value)}
                            placeholder={fmt(snap.expected_physical_cash)}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400"/>
                        {actualCash && (
                            <p className={`text-xs mt-1 font-bold ${diff >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                Difference: {diff >= 0 ? '+' : ''}{fmt(diff)} CFA
                            </p>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 mb-1 uppercase">Closing Notes</label>
                        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-400"/>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <button onClick={onClose} className="px-5 py-2 border border-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50">Cancel</button>
                        <button onClick={() => onConfirm({ actual_cash: parseFloat(actualCash)||0, notes })} disabled={saving}
                            className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-bold disabled:opacity-50">
                            {saving ? '⏳ Locking...' : '🔒 Lock Day'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
});

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const ExecutiveDashboard = () => {
    const { authFetch, selectedDepo, user, depos } = useContext(AppContext);
    const [state, dispatch] = useReducer(reducer, {
        ...initialState,
        depoId: null,
        month:  new Date().getMonth() + 1,
        year:   new Date().getFullYear(),
        date:   new Date().toISOString().split('T')[0],
    });

    const { data, loading, error, month, year, date, ledgerOpen, achatOpen, closingOpen, dayClosing, achatSaving } = state;

    // Resolve depoId from context
    const resolvedDepo = selectedDepo === 'Global' ? (user?.depo_id || 1) : selectedDepo;
    const branchName   = depos?.find(d => d.id == resolvedDepo)?.name || `Branch ${resolvedDepo}`;

    // ── Data Fetching ──────────────────────────────────────────────────────
    const fetchAll = useCallback(async () => {
        dispatch({ type: 'LOAD_START' });
        try {
            const params = new URLSearchParams({
                month, year, date,
                depo_id: resolvedDepo,
            });
            const res  = await authFetch(`/api/executive_dashboard?${params}`);
            const json = await res.json();
            if (!res.ok) throw new Error(json.message || 'Server error');
            dispatch({ type: 'LOAD_SUCCESS', payload: json });
        } catch (e) {
            dispatch({ type: 'LOAD_ERROR', payload: e.message });
        }
    }, [month, year, date, resolvedDepo, authFetch]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // ── Close Day Handler ─────────────────────────────────────────────────
    const handleConfirmClose = useCallback(async ({ actual_cash, notes }) => {
        dispatch({ type: 'SET_CLOSING', value: true });
        try {
            const res  = await authFetch('/api/executive_eod', {
                method: 'POST',
                body: JSON.stringify({ action: 'close_day', depo_id: resolvedDepo, date, actual_cash, notes }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.message);
            dispatch({ type: 'CLOSE_MODAL', modal: 'closing' });
            fetchAll();
        } catch (e) { alert(e.message); }
        finally { dispatch({ type: 'SET_CLOSING', value: false }); }
    }, [authFetch, resolvedDepo, date, fetchAll]);

    // ── Manual Achat Handler ───────────────────────────────────────────────
    const handleSaveAchat = useCallback(async (form) => {
        dispatch({ type: 'SET_SAVING', value: true });
        try {
            const res  = await authFetch('/api/executive_eod', {
                method: 'POST',
                body: JSON.stringify({ action: 'manual_achat', depo_id: resolvedDepo, date, ...form }),
            });
            const json = await res.json();
            if (!res.ok) throw new Error(json.message);
            dispatch({ type: 'CLOSE_MODAL', modal: 'achat' });
            fetchAll();
        } catch (e) { alert(e.message); }
        finally { dispatch({ type: 'SET_SAVING', value: false }); }
    }, [authFetch, resolvedDepo, date, fetchAll]);

    // ── Render ─────────────────────────────────────────────────────────────
    return (
        <div className="bg-slate-50 min-h-screen">

            {/* ── Control Bar ── */}
            <div className="print:hidden bg-white border-b border-slate-200 shadow-sm px-6 py-4">
                <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 leading-tight">Executive Financial Dashboard</h1>
                        <p className="text-slate-500 text-sm">{branchName} — Unified view: Valuation · Matrix · EOD</p>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <input type="date" value={date}
                            onChange={e => {
                                const newDate = e.target.value;
                                if (newDate) {
                                    const dObj = new Date(newDate);
                                    dispatch({ 
                                        type: 'SET_FILTER', 
                                        payload: { 
                                            date: newDate,
                                            month: dObj.getMonth() + 1,
                                            year: dObj.getFullYear()
                                        } 
                                    });
                                }
                            }}
                            className="border border-slate-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-indigo-400"/>
                        <button onClick={fetchAll}
                            className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-bold px-4 py-2 rounded-lg transition">
                            ↺ Refresh
                        </button>
                        <button onClick={() => window.print()}
                            className="bg-slate-800 hover:bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-lg flex items-center gap-1.5 transition">
                            🖨️ Print PDF
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Print Header (Removed, using custom block below) ── */}

            {/* ── Loading State ── */}
            {loading && (
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-14 w-14 border-4 border-indigo-500 border-t-transparent mx-auto mb-4"></div>
                        <p className="text-slate-500 font-medium">Loading executive data…</p>
                    </div>
                </div>
            )}

            {/* ── Error State ── */}
            {error && !loading && (
                <div className="max-w-screen-xl mx-auto p-8">
                    <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-red-700">
                        <p className="font-bold text-lg">⚠️ Failed to load dashboard</p>
                        <p className="text-sm mt-1">{error}</p>
                        <button onClick={fetchAll} className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-bold">Retry</button>
                    </div>
                </div>
            )}

            {/* ── Main Content (Hidden on Print) ── */}
            {!loading && !error && data && (
                <div className="max-w-screen-xl mx-auto px-4 md:px-6 py-8 print:hidden space-y-12">

                    {/* SECTION 1 */}
                    <Section1Network data={data.section1_network} />

                    <hr className="border-slate-200 print:border-slate-400" />

                    {/* SECTION 2 */}
                    <Section2Matrix
                        data={data.section2_matrix}
                        month={month} year={year}
                        onMonthChange={v => {
                            const newDate = `${year}-${String(v).padStart(2, '0')}-01`;
                            dispatch({ type: 'SET_FILTER', payload: { month: v, date: newDate } });
                        }}
                        onYearChange={v  => {
                            const newDate = `${v}-${String(month).padStart(2, '0')}-01`;
                            dispatch({ type: 'SET_FILTER', payload: { year: v, date: newDate } });
                        }}
                    />

                    <hr className="border-slate-200 print:border-slate-400" />

                    {/* SECTION 3 */}
                    <Section3EOD
                        data={data.section3_eod}
                        onOpenLedger={() => dispatch({ type: 'OPEN_MODAL', modal: 'ledger' })}
                        onOpenAchat={()  => dispatch({ type: 'OPEN_MODAL', modal: 'achat' })}
                        onCloseDay={()   => dispatch({ type: 'OPEN_MODAL', modal: 'closing' })}
                        dayClosing={dayClosing}
                    />

                </div>
            )}

            {/* ── DEDICATED PRINT LAYOUT ── */}
            {!loading && !error && data && (
                <div className="hidden print:block p-8 max-w-full">
                    {/* Header */}
                    <div className="mb-10 border-b-4 border-slate-800 pb-4 flex justify-between items-end">
                        <div>
                            <h1 className="text-5xl font-black text-slate-900 mb-2">Executive Summary</h1>
                            <h2 className="text-2xl text-slate-600 font-bold">Multi-Branch Financial Overview</h2>
                        </div>
                        <div className="text-right text-slate-500 font-bold text-lg">
                            Period: {MONTHS_FULL[month-1]} {year}
                        </div>
                    </div>

                    {/* Top KPIs */}
                    <div className="grid grid-cols-2 gap-8 mb-10">
                        <div className="border-2 border-slate-200 p-8 rounded-2xl text-center shadow-sm">
                            <div className="text-slate-500 font-bold mb-3 uppercase tracking-wider text-xl">Total Monthly Revenue</div>
                            <div className="text-6xl font-black text-slate-800">{fmt(data.section1_network.totals.monthly_revenue)}</div>
                        </div>
                        <div className="border-2 border-slate-200 p-8 rounded-2xl text-center shadow-sm">
                            <div className="text-slate-500 font-bold mb-3 uppercase tracking-wider text-xl">Net Gain</div>
                            <div className={`text-6xl font-black ${data.section1_network.totals.net_profit_loss >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {data.section1_network.totals.net_profit_loss > 0 ? '+' : ''}{fmt(data.section1_network.totals.net_profit_loss)}
                            </div>
                        </div>
                    </div>

                    {/* Forecasting Widget */}
                    {(() => {
                        const now = new Date();
                        // Only show projection if viewing the current month/year
                        if (now.getMonth() + 1 === month && now.getFullYear() === year) {
                            const daysPassed = now.getDate();
                            const totalDays = new Date(year, month, 0).getDate();
                            
                            if (daysPassed > 0) {
                                const projection = (data.section1_network.totals.monthly_revenue / daysPassed) * totalDays;
                                return (
                                    <div className="bg-indigo-50/70 border border-indigo-200 p-6 rounded-xl mb-6 flex flex-row items-center justify-between">
                                        <div>
                                            <h3 className="text-xl font-bold text-indigo-900 mb-1">End of Month Projection</h3>
                                            <p className="text-indigo-700 text-sm font-medium">Calculated run-rate based on {daysPassed} days passed out of {totalDays}</p>
                                        </div>
                                        <div className="text-3xl font-bold text-indigo-700">
                                            {fmt(projection)}
                                        </div>
                                    </div>
                                );
                            }
                        }
                        return null;
                    })()}

                    {/* Simplified Branch Table */}
                    <div className="mb-10">
                        <h3 className="text-3xl font-bold text-slate-800 mb-6">Branch Performance</h3>
                        <div className="w-full overflow-x-auto max-w-full">
<table className="w-full text-left border-collapse border-2 border-slate-300">
                            <thead>
                                <tr className="bg-slate-100 text-slate-800 text-xl border-b-2 border-slate-300">
                                    <th className="p-5 font-bold">Branch Name</th>
                                    <th className="p-5 font-bold text-right border-l-2 border-slate-300">Stock Cost</th>
                                    <th className="p-5 font-bold text-right border-l-2 border-slate-300">Expected Sell Value</th>
                                    <th className="p-5 font-bold text-right border-l-2 border-slate-300">Actual Revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.section1_network.branches.map((b, idx) => (
                                    <tr key={b.branch_id || idx} className="border-b-2 border-slate-200 text-lg">
                                        <td className="p-5 font-bold text-slate-800">{b.branch_name}</td>
                                        <td className="p-5 text-right font-medium text-slate-600 border-l-2 border-slate-200">{fmt(b.stock_cost_value)}</td>
                                        <td className="p-5 text-right font-bold text-indigo-600 border-l-2 border-slate-200">{fmt(b.expected_sell_value)}</td>
                                        <td className="p-5 text-right font-black text-slate-800 border-l-2 border-slate-200">{fmt(b.monthly_revenue)}</td>
                                    </tr>
                                ))}
                                <tr className="bg-slate-800 text-white text-xl font-black">
                                    <td className="p-5">NETWORK TOTAL</td>
                                    <td className="p-5 text-right border-l-2 border-slate-600">{fmt(data.section1_network.totals.stock_cost_value)}</td>
                                    <td className="p-5 text-right border-l-2 border-slate-600">{fmt(data.section1_network.totals.expected_sell_value)}</td>
                                    <td className="p-5 text-right border-l-2 border-slate-600">{fmt(data.section1_network.totals.monthly_revenue)}</td>
                                </tr>
                            </tbody>
                        </table>
</div>
                    </div>
                </div>
            )}
            {/* ── Modals ── */}
            {ledgerOpen && data && (
                <LedgerModal
                    entries={data.section3_eod.ledger_entries}
                    date={data.section3_eod.date}
                    onClose={() => dispatch({ type: 'CLOSE_MODAL', modal: 'ledger' })}
                />
            )}
            {achatOpen && data && (
                <ManualAchatModal
                    date={data.section3_eod.date}
                    saving={achatSaving}
                    onSave={handleSaveAchat}
                    onClose={() => dispatch({ type: 'CLOSE_MODAL', modal: 'achat' })}
                />
            )}
            {closingOpen && data && (
                <CloseDayModal
                    snap={data.section3_eod.live_snapshot}
                    saving={dayClosing}
                    onConfirm={handleConfirmClose}
                    onClose={() => dispatch({ type: 'CLOSE_MODAL', modal: 'closing' })}
                />
            )}
        </div>
    );
};

export default ExecutiveDashboard;
